import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  LayoutGrid, GripVertical, Eye, EyeOff, RefreshCw, 
  Save, RotateCcw, Activity, AlertTriangle, TrendingUp,
  ClipboardList, Lightbulb, Shield, Users, Gauge, Palette
} from 'lucide-react';
import { ThemeSelector } from '@/components/ThemeSelector';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WIDGET_TYPES = {
  quality_score: { icon: Gauge, label: 'Quality Score', color: 'text-emerald-500' },
  metrics_summary: { icon: Activity, label: 'Metrics Summary', color: 'text-blue-500' },
  alerts: { icon: AlertTriangle, label: 'Alerts & Warnings', color: 'text-orange-500' },
  trends: { icon: TrendingUp, label: 'Trend Charts', color: 'text-purple-500' },
  pips: { icon: ClipboardList, label: 'Active PIPs', color: 'text-cyan-500' },
  insights: { icon: Lightbulb, label: 'AI Insights', color: 'text-yellow-500' },
  compliance: { icon: Shield, label: 'Compliance Status', color: 'text-green-500' },
  staff_health: { icon: Users, label: 'Staff Health', color: 'text-pink-500' }
};

const WidgetCard = ({ widget, onToggle, onSizeChange, onMove }) => {
  const config = WIDGET_TYPES[widget.widget_type] || { icon: Activity, label: widget.widget_type, color: 'text-gray-500' };
  const Icon = config.icon;
  
  return (
    <div className={`flex items-center gap-4 p-4 rounded-lg border ${widget.visible ? 'bg-card' : 'bg-muted/50 opacity-60'}`}>
      <div className="cursor-grab">
        <GripVertical className="h-5 w-5 text-muted-foreground" />
      </div>
      
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${widget.visible ? 'bg-primary/10' : 'bg-muted'}`}>
        <Icon className={`h-5 w-5 ${widget.visible ? config.color : 'text-muted-foreground'}`} />
      </div>
      
      <div className="flex-1">
        <p className="font-medium">{config.label}</p>
        <p className="text-xs text-muted-foreground">Position: {widget.position + 1}</p>
      </div>
      
      <Select value={widget.size} onValueChange={(v) => onSizeChange(widget.widget_type, v)}>
        <SelectTrigger className="w-24">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="small">Small</SelectItem>
          <SelectItem value="medium">Medium</SelectItem>
          <SelectItem value="large">Large</SelectItem>
        </SelectContent>
      </Select>
      
      <div className="flex items-center gap-2">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => onToggle(widget.widget_type)}
          data-testid={`toggle-${widget.widget_type}`}
        >
          {widget.visible ? (
            <Eye className="h-4 w-4 text-green-500" />
          ) : (
            <EyeOff className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
      </div>
    </div>
  );
};

export default function DashboardSettings() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [widgets, setWidgets] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
        setWidgets(data.widgets || []);
        setAutoRefresh(data.auto_refresh ?? true);
        setRefreshInterval(data.refresh_interval || 30);
      }
    } catch (error) {
      toast.error('Failed to load dashboard configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, [token]);

  const toggleWidget = (widgetType) => {
    setWidgets(prev => prev.map(w => 
      w.widget_type === widgetType ? { ...w, visible: !w.visible } : w
    ));
    setHasChanges(true);
  };

  const changeSize = (widgetType, size) => {
    setWidgets(prev => prev.map(w => 
      w.widget_type === widgetType ? { ...w, size } : w
    ));
    setHasChanges(true);
  };

  const moveWidget = (widgetType, direction) => {
    const idx = widgets.findIndex(w => w.widget_type === widgetType);
    if ((direction === -1 && idx === 0) || (direction === 1 && idx === widgets.length - 1)) return;
    
    const newWidgets = [...widgets];
    const temp = newWidgets[idx];
    newWidgets[idx] = newWidgets[idx + direction];
    newWidgets[idx + direction] = temp;
    
    // Update positions
    newWidgets.forEach((w, i) => w.position = i);
    
    setWidgets(newWidgets);
    setHasChanges(true);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/config`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          widgets,
          auto_refresh: autoRefresh,
          refresh_interval: refreshInterval
        })
      });

      if (response.ok) {
        toast.success('Dashboard configuration saved');
        setHasChanges(false);
      } else {
        toast.error('Failed to save configuration');
      }
    } catch (error) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const resetConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dashboard/config/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setWidgets(data.widgets);
        setAutoRefresh(true);
        setRefreshInterval(30);
        setHasChanges(false);
        toast.success('Dashboard reset to defaults');
      }
    } catch (error) {
      toast.error('Reset failed');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-settings-loading">
        <Skeleton className="h-10 w-64" />
        <div className="space-y-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-20" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <LayoutGrid className="h-8 w-8 text-primary" />
            Dashboard Settings
          </h1>
          <p className="text-muted-foreground mt-1">Customize your dashboard layout and widgets</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetConfig}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Default
          </Button>
          <Button onClick={saveConfig} disabled={saving || !hasChanges} data-testid="save-config-btn">
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {hasChanges && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-yellow-500" />
          <span className="text-sm">You have unsaved changes</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Widget Configuration */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Widget Layout</CardTitle>
              <CardDescription>
                Toggle visibility and change sizes for dashboard widgets
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {widgets.sort((a, b) => a.position - b.position).map((widget) => (
                <WidgetCard 
                  key={widget.widget_type}
                  widget={widget}
                  onToggle={toggleWidget}
                  onSizeChange={changeSize}
                  onMove={moveWidget}
                />
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Settings Panel */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5" />
                Auto-Refresh
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="auto-refresh">Enable auto-refresh</Label>
                <Switch
                  id="auto-refresh"
                  checked={autoRefresh}
                  onCheckedChange={(v) => { setAutoRefresh(v); setHasChanges(true); }}
                  data-testid="auto-refresh-switch"
                />
              </div>
              
              {autoRefresh && (
                <div className="space-y-2">
                  <Label>Refresh interval</Label>
                  <Select 
                    value={refreshInterval.toString()} 
                    onValueChange={(v) => { setRefreshInterval(parseInt(v)); setHasChanges(true); }}
                  >
                    <SelectTrigger data-testid="refresh-interval-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 seconds</SelectItem>
                      <SelectItem value="30">30 seconds</SelectItem>
                      <SelectItem value="60">1 minute</SelectItem>
                      <SelectItem value="120">2 minutes</SelectItem>
                      <SelectItem value="300">5 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Theme Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Theme & Appearance
              </CardTitle>
              <CardDescription>
                Customize the color theme and appearance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ThemeSelector variant="full" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Preview</CardTitle>
              <CardDescription>
                Active widgets on your dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {widgets.filter(w => w.visible).map(widget => {
                  const config = WIDGET_TYPES[widget.widget_type];
                  return (
                    <Badge key={widget.widget_type} variant="secondary" className="gap-1">
                      {config?.label || widget.widget_type}
                    </Badge>
                  );
                })}
              </div>
              {widgets.filter(w => w.visible).length === 0 && (
                <p className="text-sm text-muted-foreground">No widgets visible</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
